import React, { useState, useEffect, useRef } from 'react';
import useAuth from '../../hooks/useAuth';
import fileSharingService from '../../services/fileSharingApi';
import KerrscreenArtFilesDashboard from '../KerrscreenArtFiles/Dashboard';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import mammoth from 'mammoth';
import './Dashboard.css';

const Dashboard = () => {
  const { user, hasPermission } = useAuth();
  const fileInputRef = useRef(null);

  const hasFileSharing = hasPermission('file-sharing:read');
  const hasKerrscreen = hasPermission('kerrscreen:read');

  // Tab state
  const [activeTab, setActiveTab] = useState(hasFileSharing ? 'general' : 'art');

  // View state
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Navigation state
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([]);

  // Data state
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Upload state
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [draggingFile, setDraggingFile] = useState(null);

  // Multi-select state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  // Modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [createFolderModalOpen, setCreateFolderModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [sharedUsers, setSharedUsers] = useState([]);
  const [newName, setNewName] = useState('');
  const [newFolderName, setNewFolderName] = useState('');

  // Pagination state
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 1 });

  // Stats state
  const [stats, setStats] = useState({
    totalFiles: 0,
    totalSize: 0,
    storageLimit: 10 * 1024 * 1024 * 1024, // 10GB default
    globalUsage: 0,
  });

  // Fetch files on mount and when folder/page changes
  useEffect(() => {
    fetchFiles();
    fetchStats();
  }, [currentFolderId, pagination.page]);

  const fetchFiles = async (page = pagination.page) => {
    try {
      setLoading(true);
      const response = await fileSharingService.getAll({
        folderId: currentFolderId,
        page,
        limit: pagination.limit,
        search: searchTerm || undefined
      });
      const data = response.data;
      setFiles(data.files || []);
      if (data.pagination) {
        setPagination(data.pagination);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Failed to load files. Please try again.');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const fetchStats = async () => {
    try {
      const response = await fileSharingService.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Folder navigation
  const openFolder = (folder) => {
    setCurrentFolderId(folder.id);
    setFolderPath([...folderPath, { id: folder.id, name: folder.original_name }]);
  };

  const navigateToFolder = (index) => {
    if (index === -1) {
      // Go to root
      setCurrentFolderId(null);
      setFolderPath([]);
    } else {
      // Go to specific folder in path
      const folder = folderPath[index];
      setCurrentFolderId(folder.id);
      setFolderPath(folderPath.slice(0, index + 1));
    }
    // Clear selection when navigating
    setSelectedFiles([]);
  };

  // Multi-select handlers
  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev =>
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const selectAll = () => {
    const selectableFiles = filteredFiles.filter(f => !f.is_folder).map(f => f.id);
    setSelectedFiles(selectableFiles);
  };

  const clearSelection = () => {
    setSelectedFiles([]);
    setIsSelectionMode(false);
  };

  const isFileSelected = (fileId) => {
    return selectedFiles.includes(fileId);
  };

  // Folder creation
  const openCreateFolderModal = () => {
    setCreateFolderModalOpen(true);
    setNewFolderName('');
    setError(null);
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();

    if (!newFolderName.trim()) {
      setError('Folder name cannot be empty');
      return;
    }

    try {
      await fileSharingService.createFolder({
        name: newFolderName,
        parentFolderId: currentFolderId
      });
      setSuccess('Folder created successfully');
      setCreateFolderModalOpen(false);
      setNewFolderName('');
      fetchFiles();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error creating folder:', err);
      setError(err.response?.data?.error || 'Failed to create folder');
    }
  };

  // File upload handlers
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    uploadFiles(selectedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    uploadFiles(droppedFiles);
  };

  // File drag handlers
  const handleFileDragStart = (e, file) => {
    if (file.is_folder) return;

    // If dragging a selected file, drag all selected files
    // Otherwise just drag this one file
    if (selectedFiles.includes(file.id) && selectedFiles.length > 1) {
      console.log('Dragging multiple files:', selectedFiles.length);
      setDraggingFile({ id: 'multiple', count: selectedFiles.length, ids: selectedFiles });
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', JSON.stringify(selectedFiles));
    } else {
      console.log('Dragging single file:', file.original_name);
      setDraggingFile(file);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', file.id);
    }
  };

  const handleFileDragEnd = () => {
    setDraggingFile(null);
  };

  // Folder drag and drop handlers
  const handleFolderDragOver = (e, folderId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(folderId);
  };

  const handleFolderDragLeave = (e, folderId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);
  };

  const handleFolderDrop = async (e, folder) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolderId(null);

    console.log('Dropped onto folder:', folder.original_name);
    console.log('draggingFile:', draggingFile);

    // Check if we're dragging an existing file or uploading new files
    if (draggingFile) {
      // Moving existing file(s)
      try {
        if (draggingFile.id === 'multiple') {
          // Moving multiple selected files
          console.log('Moving multiple files:', draggingFile.ids);
          const fileIds = draggingFile.ids;
          let successCount = 0;
          let failCount = 0;

          for (const fileId of fileIds) {
            try {
              await fileSharingService.move(fileId, folder.id);
              successCount++;
            } catch (err) {
              console.error(`Error moving file ${fileId}:`, err);
              failCount++;
            }
          }

          if (successCount > 0) {
            setSuccess(`Moved ${successCount} file(s) to "${folder.original_name}"`);
            setTimeout(() => setSuccess(null), 3000);
          }
          if (failCount > 0) {
            setError(`Failed to move ${failCount} file(s)`);
            setTimeout(() => setError(null), 5000);
          }

          // Clear selection after moving
          setSelectedFiles([]);
        } else {
          // Moving a single file
          await fileSharingService.move(draggingFile.id, folder.id);
          setSuccess(`Moved "${draggingFile.original_name}" to "${folder.original_name}"`);
          setTimeout(() => setSuccess(null), 3000);
        }

        fetchFiles();
      } catch (err) {
        console.error('Error moving file:', err);
        setError(err.response?.data?.error || 'Failed to move file');
      }
      setDraggingFile(null);
    } else {
      // Uploading new files from computer
      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        await uploadFiles(droppedFiles, folder.id);
      }
    }
  };

  const uploadFiles = async (filesToUpload, targetFolderId = null) => {
    if (!hasPermission('file-sharing:write')) {
      setError('You do not have permission to upload files.');
      return;
    }

    // Client-side quota pre-check
    const totalUploadSize = filesToUpload.reduce((sum, f) => sum + f.size, 0);
    const remaining = stats.storageLimit - (stats.globalUsage || stats.totalSize);
    if (totalUploadSize > remaining) {
      setError(`Not enough storage space. You need ${formatFileSize(totalUploadSize)} but only ${formatFileSize(remaining)} is available.`);
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    const uploadFolderId = targetFolderId !== null ? targetFolderId : currentFolderId;
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (const file of filesToUpload) {
      try {
        console.log('Uploading file:', {
          name: file.name,
          type: file.type,
          size: file.size,
          folderId: uploadFolderId
        });

        const formData = new FormData();
        formData.append('file', file);
        if (uploadFolderId) {
          formData.append('parentFolderId', uploadFolderId);
        }

        const response = await fileSharingService.upload(formData);
        console.log('Upload successful:', response.data);
        successCount++;
      } catch (err) {
        console.error('Error uploading file:', err);
        console.error('Error response:', err.response?.data);
        const errorMessage = err.response?.data?.error || err.response?.data?.details || err.message || 'Unknown error';
        errors.push(`${file.name}: ${errorMessage}`);
        failCount++;
      }
    }

    setUploading(false);

    // Show results
    if (successCount > 0 && failCount === 0) {
      setSuccess(`Successfully uploaded ${successCount} file(s)`);
      setTimeout(() => setSuccess(null), 5000);
    } else if (failCount > 0 && successCount === 0) {
      setError(`Failed to upload all files:\n${errors.join('\n')}`);
      // Don't auto-clear error messages
    } else if (successCount > 0 && failCount > 0) {
      setSuccess(`Uploaded ${successCount} file(s)`);
      setError(`Failed to upload ${failCount} file(s):\n${errors.join('\n')}`);
      setTimeout(() => setSuccess(null), 5000);
    }

    // Wait a moment for database to commit, then refresh
    setTimeout(() => {
      fetchFiles();
      fetchStats();
    }, 500);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // File rename
  const openRenameModal = (file) => {
    setSelectedFile(file);
    setNewName(file.original_name);
    setRenameModalOpen(true);
    setError(null);
  };

  const handleRename = async (e) => {
    e.preventDefault();

    if (!newName.trim()) {
      setError('Name cannot be empty');
      return;
    }

    try {
      await fileSharingService.rename(selectedFile.id, newName);
      setSuccess('Renamed successfully');
      setRenameModalOpen(false);
      fetchFiles();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error renaming:', err);
      setError(err.response?.data?.error || 'Failed to rename');
    }
  };

  // File preview
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [documentContent, setDocumentContent] = useState(null);

  const openPreviewModal = async (file) => {
    setSelectedFile(file);
    setPreviewModalOpen(true);
    setLoadingPreview(true);
    setPreviewUrl(null);
    setDocumentContent(null);

    try {
      console.log('Opening preview for file:', file);
      const response = await fileSharingService.download(file.id);
      console.log('Download response:', response);
      console.log('Response data type:', typeof response.data);
      console.log('Response data instanceof Blob:', response.data instanceof Blob);
      const fileName = file.original_name || '';
      const fileExt = fileName.split('.').pop().toLowerCase();
      const mimeType = file.mime_type || '';

      // Detect file type using MIME type and extension
      const isCSV = mimeType === 'text/csv' || fileExt === 'csv';
      const isExcel = mimeType === 'application/vnd.ms-excel' ||
                      mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                      ['xls', 'xlsx'].includes(fileExt);
      const isWord = mimeType === 'application/msword' ||
                     mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                     ['doc', 'docx'].includes(fileExt);

      // Handle different document types
      if (isCSV) {
        // Parse CSV
        const text = await response.data.text();
        Papa.parse(text, {
          complete: (results) => {
            setDocumentContent({ type: 'csv', data: results.data });
            setLoadingPreview(false);
          },
          error: (error) => {
            console.error('CSV parse error:', error);
            setError('Failed to parse CSV file');
            setLoadingPreview(false);
          }
        });
        return;
      } else if (isExcel) {
        // Parse Excel
        const arrayBuffer = await response.data.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        setDocumentContent({ type: 'excel', data, sheetName: firstSheetName });
      } else if (isWord) {
        // Parse Word document
        const arrayBuffer = await response.data.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setDocumentContent({ type: 'word', html: result.value });
      } else {
        // For other file types (images, videos, PDFs, etc.), create blob URL
        // response.data is already a Blob from responseType: 'blob'
        const url = window.URL.createObjectURL(response.data);
        setPreviewUrl(url);
      }
    } catch (err) {
      console.error('Error loading preview:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });
      setError(`Failed to load preview: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  const canPreview = (file) => {
    if (file.is_folder) return false;
    const mimeType = file.mime_type || '';
    const fileName = file.original_name || '';
    const fileExt = fileName.split('.').pop().toLowerCase();

    // Check by MIME type
    const canPreviewByMime = (
      mimeType.startsWith('image/') ||
      mimeType.startsWith('video/') ||
      mimeType.startsWith('audio/') ||
      mimeType === 'application/pdf' ||
      mimeType === 'application/x-pdf' ||
      mimeType === 'application/acrobat' ||
      mimeType === 'application/x-download' ||
      mimeType.startsWith('text/') ||
      mimeType === 'text/csv' ||
      mimeType === 'application/vnd.ms-excel' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      mimeType === 'application/msword' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    );

    // Also check by file extension as fallback
    const previewableExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg',
      'mp4', 'webm', 'ogg', 'mp3', 'wav', 'txt', 'csv', 'xls', 'xlsx', 'doc', 'docx'];
    const canPreviewByExt = previewableExtensions.includes(fileExt);

    return canPreviewByMime || canPreviewByExt;
  };

  // Helper function to detect file type
  const getFileType = (file) => {
    const mimeType = file.mime_type || '';
    const fileName = file.original_name || '';
    const fileExt = fileName.split('.').pop().toLowerCase();

    // Detect PDF (by MIME or extension)
    if (mimeType === 'application/pdf' || mimeType === 'application/x-pdf' ||
        mimeType === 'application/acrobat' || mimeType === 'application/x-download' ||
        fileExt === 'pdf') {
      return 'pdf';
    }

    // Detect images
    if (mimeType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileExt)) {
      return 'image';
    }

    // Detect videos
    if (mimeType.startsWith('video/') || ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(fileExt)) {
      return 'video';
    }

    // Detect audio
    if (mimeType.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'aac'].includes(fileExt)) {
      return 'audio';
    }

    // Detect CSV
    if (mimeType === 'text/csv' || fileExt === 'csv') {
      return 'csv';
    }

    // Detect Excel
    if (mimeType === 'application/vnd.ms-excel' ||
        mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        ['xls', 'xlsx'].includes(fileExt)) {
      return 'excel';
    }

    // Detect Word
    if (mimeType === 'application/msword' ||
        mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        ['doc', 'docx'].includes(fileExt)) {
      return 'word';
    }

    // Detect text
    if (mimeType.startsWith('text/') || fileExt === 'txt') {
      return 'text';
    }

    return 'unknown';
  };

  const renderPreview = (file) => {
    if (!file || file.is_folder) return null;

    if (loadingPreview) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner"></div>
          <p>Loading preview...</p>
        </div>
      );
    }

    const fileType = getFileType(file);

    // Handle document content (CSV, Excel, Word)
    if (documentContent) {
      if (documentContent.type === 'csv' || documentContent.type === 'excel') {
        const data = documentContent.data;
        if (!data || data.length === 0) {
          return <p>Empty document</p>;
        }

        return (
          <div style={{ overflowX: 'auto', maxHeight: '70vh', padding: '20px' }}>
            {documentContent.type === 'excel' && (
              <h3 style={{ marginTop: 0 }}>Sheet: {documentContent.sheetName}</h3>
            )}
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              border: '1px solid #ddd',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  {data[0]?.map((header, index) => (
                    <th key={index} style={{
                      border: '1px solid #ddd',
                      padding: '8px 12px',
                      textAlign: 'left',
                      fontWeight: '600'
                    }}>
                      {header || `Column ${index + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.slice(1).map((row, rowIndex) => (
                  <tr key={rowIndex} style={{
                    backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f8f9fa'
                  }}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} style={{
                        border: '1px solid #ddd',
                        padding: '8px 12px'
                      }}>
                        {cell !== null && cell !== undefined ? String(cell) : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      } else if (documentContent.type === 'word') {
        return (
          <div
            style={{
              padding: '20px 40px',
              maxHeight: '70vh',
              overflowY: 'auto',
              backgroundColor: 'white',
              lineHeight: '1.6'
            }}
            dangerouslySetInnerHTML={{ __html: documentContent.html }}
          />
        );
      }
    }

    // Handle blob URLs for other file types
    if (!previewUrl) {
      return <p>Failed to load preview. Please try downloading the file.</p>;
    }

    if (fileType === 'image') {
      return (
        <img
          src={previewUrl}
          alt={file.original_name}
          style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
        />
      );
    } else if (fileType === 'video') {
      return <video controls style={{ maxWidth: '100%', maxHeight: '70vh' }} src={previewUrl} />;
    } else if (fileType === 'audio') {
      return <audio controls style={{ width: '100%' }} src={previewUrl} />;
    } else if (fileType === 'pdf') {
      return <iframe src={previewUrl} title={file.original_name} style={{ width: '100%', height: '70vh', border: 'none' }} />;
    } else if (fileType === 'text') {
      return <iframe src={previewUrl} title={file.original_name} style={{ width: '100%', height: '70vh', border: 'none' }} />;
    }

    return <p>Preview not available for this file type.</p>;
  };

  // File actions
  const handleDownload = async (file) => {
    try {
      const response = await fileSharingService.download(file.id);
      // response.data is already a Blob from responseType: 'blob'
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
    }
  };

  const handleDelete = async (file) => {
    if (!hasPermission('file-sharing:write') && file.uploaded_by !== user?.id) {
      setError('You do not have permission to delete this file.');
      return;
    }

    const itemType = file.is_folder ? 'folder' : 'file';
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this ${itemType} "${file.original_name}"? This action cannot be undone.`
    );

    if (confirmDelete) {
      try {
        await fileSharingService.delete(file.id);
        setSuccess(`${itemType} deleted successfully`);
        fetchFiles();
        fetchStats();
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        console.error('Error deleting file:', err);
        setError(`Failed to delete ${itemType}`);
      }
    }
  };

  const openShareModal = async (file) => {
    setSelectedFile(file);
    setShareModalOpen(true);

    try {
      const response = await fileSharingService.getSharedUsers(file.id);
      setSharedUsers(response.data || []);
    } catch (err) {
      console.error('Error fetching shared users:', err);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();

    if (!shareEmail) {
      setError('Please enter an email address');
      return;
    }

    try {
      await fileSharingService.share(selectedFile.id, { email: shareEmail });
      setSuccess('File shared successfully');
      setShareEmail('');

      // Refresh shared users list
      const response = await fileSharingService.getSharedUsers(selectedFile.id);
      setSharedUsers(response.data || []);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error sharing file:', err);
      setError(err.response?.data?.error || 'Failed to share file');
    }
  };

  const closeShareModal = () => {
    setShareModalOpen(false);
    setSelectedFile(null);
    setShareEmail('');
    setSharedUsers([]);
    setError(null);
  };

  const closePreviewModal = () => {
    // Clean up blob URL to prevent memory leaks
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setDocumentContent(null);
    setPreviewModalOpen(false);
    setSelectedFile(null);
  };

  const closeRenameModal = () => {
    setRenameModalOpen(false);
    setSelectedFile(null);
    setNewName('');
    setError(null);
  };

  // Filter and sort files
  const filteredFiles = files
    .filter(file =>
      file.original_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.created_at) - new Date(a.created_at);
      } else if (sortBy === 'oldest') {
        return new Date(a.created_at) - new Date(b.created_at);
      } else if (sortBy === 'name-asc') {
        return a.original_name.localeCompare(b.original_name);
      } else if (sortBy === 'name-desc') {
        return b.original_name.localeCompare(a.original_name);
      } else if (sortBy === 'size-asc') {
        return (a.size || 0) - (b.size || 0);
      } else if (sortBy === 'size-desc') {
        return (b.size || 0) - (a.size || 0);
      }
      return 0;
    });

  // Helper functions
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (file) => {
    if (file.is_folder) return '📁';

    const filename = file.original_name || '';
    const ext = filename.split('.').pop().toLowerCase();
    const iconMap = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      ppt: '📊',
      pptx: '📊',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      zip: '🗜️',
      rar: '🗜️',
      mp4: '🎥',
      mp3: '🎵',
      txt: '📃',
    };
    return iconMap[ext] || '📁';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Show tabs when user has kerrscreen permission
  const showTabs = hasKerrscreen;

  const renderTabs = () => (
    <div className="file-sharing-tabs">
      {hasFileSharing && (
        <button
          className={`tab-btn ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          <span className="tab-icon">📁</span>
          <span className="tab-label">General Files</span>
        </button>
      )}
      <button
        className={`tab-btn ${activeTab === 'art' ? 'active' : ''}`}
        onClick={() => setActiveTab('art')}
      >
        <span className="tab-icon">🎨</span>
        <span className="tab-label">Kerr Screen Art Files</span>
      </button>
    </div>
  );

  if (activeTab === 'general' && loading) {
    return (
      <div className="file-sharing-container">
        {showTabs && renderTabs()}
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading files...</p>
        </div>
      </div>
    );
  }

  const storagePercent = (stats.totalSize / stats.storageLimit) * 100;

  // Art Files tab
  if (activeTab === 'art') {
    return (
      <div className="file-sharing-container">
        {showTabs && renderTabs()}
        <KerrscreenArtFilesDashboard />
      </div>
    );
  }

  return (
    <div className="file-sharing-container">
      {/* Tab Bar */}
      {showTabs && renderTabs()}

      {/* Header */}
      <div className="file-sharing-header">
        <div className="header-content">
          <div className="search-box">
            <input
              type="text"
              className="search-input"
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPagination(prev => ({ ...prev, page: 1 }));
                  fetchFiles(1);
                }
              }}
            />
          </div>
          {hasPermission('file-sharing:write') && (
            <>
              <button className="btn-upload" onClick={openCreateFolderModal}>
                📁 New Folder
              </button>
              <button className="btn-upload" onClick={handleFileSelect}>
                ⬆ Upload Files
              </button>
            </>
          )}
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        className="file-input"
      />

      {/* Breadcrumb Navigation */}
      {folderPath.length > 0 && (
        <div className="breadcrumb-nav">
          <button onClick={() => navigateToFolder(-1)} className="breadcrumb-item">
            🏠 Home
          </button>
          {folderPath.map((folder, index) => (
            <React.Fragment key={folder.id}>
              <span className="breadcrumb-separator">/</span>
              <button
                onClick={() => navigateToFolder(index)}
                className="breadcrumb-item"
              >
                {folder.name}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="alert alert-error" style={{ whiteSpace: 'pre-line', position: 'relative', paddingRight: '40px' }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              position: 'absolute',
              right: '12px',
              top: '12px',
              background: 'transparent',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#721c24'
            }}
          >
            ×
          </button>
        </div>
      )}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Upload Drop Zone */}
      {hasPermission('file-sharing:write') && (
        <div
          className={`upload-dropzone ${isDragging ? 'drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleFileSelect}
        >
          <div className="upload-icon">☁️</div>
          <h3>Drag and drop files here</h3>
          <p>or click to browse</p>
          {uploading && <p>Uploading...</p>}
        </div>
      )}

      {/* Storage Stats */}
      <div className="storage-stats">
        <div className="storage-header">
          <h3>Storage</h3>
          <span className="storage-percent">{storagePercent.toFixed(1)}%</span>
        </div>
        <div className="storage-bar">
          <div className="storage-fill" style={{ width: `${storagePercent}%` }}></div>
        </div>
        <div className="storage-info">
          <span>{formatFileSize(stats.totalSize)} used</span>
          <span>{formatFileSize(stats.storageLimit)} total</span>
        </div>
      </div>

      {/* Files Section */}
      <div className="files-section">
        <div className="section-header">
          <h2>
            {currentFolderId ? folderPath[folderPath.length - 1]?.name : 'My Files'} ({filteredFiles.length})
          </h2>
          <div className="filter-controls">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="size-asc">Size (Smallest)</option>
              <option value="size-desc">Size (Largest)</option>
            </select>
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                ⊞ Grid
              </button>
              <button
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List view"
              >
                ☰ List
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions Toolbar */}
        {selectedFiles.length > 0 && (
          <div className="bulk-actions-toolbar">
            <div className="bulk-actions-info">
              <span>{selectedFiles.length} file(s) selected</span>
            </div>
            <div className="bulk-actions-buttons">
              <button className="btn btn-secondary" onClick={clearSelection}>
                Clear Selection
              </button>
              {hasPermission('file-sharing:write') && (
                <button
                  className="btn btn-danger"
                  onClick={async () => {
                    if (window.confirm(`Delete ${selectedFiles.length} selected file(s)?`)) {
                      let successCount = 0;
                      for (const fileId of selectedFiles) {
                        try {
                          await fileSharingService.delete(fileId);
                          successCount++;
                        } catch (err) {
                          console.error(`Error deleting file ${fileId}:`, err);
                        }
                      }
                      setSuccess(`Deleted ${successCount} file(s)`);
                      setTimeout(() => setSuccess(null), 3000);
                      setSelectedFiles([]);
                      fetchFiles();
                      fetchStats();
                    }
                  }}
                >
                  Delete Selected
                </button>
              )}
            </div>
          </div>
        )}

        {/* Grid View */}
        {viewMode === 'grid' && (
          <div className="files-grid">
            {filteredFiles.length > 0 ? (
              filteredFiles.map((file) => (
                <div
                  key={file.id}
                  className={`file-card ${file.is_folder && dragOverFolderId === file.id ? 'drag-over-folder' : ''} ${draggingFile?.id === file.id ? 'dragging' : ''} ${!file.is_folder && isFileSelected(file.id) ? 'selected' : ''}`}
                  onDoubleClick={() => file.is_folder ? openFolder(file) : canPreview(file) && openPreviewModal(file)}
                  draggable={!file.is_folder && hasPermission('file-sharing:write')}
                  onDragStart={(e) => handleFileDragStart(e, file)}
                  onDragEnd={handleFileDragEnd}
                  {...(file.is_folder && hasPermission('file-sharing:write') ? {
                    onDragOver: (e) => handleFolderDragOver(e, file.id),
                    onDragLeave: (e) => handleFolderDragLeave(e, file.id),
                    onDrop: (e) => handleFolderDrop(e, file)
                  } : {})}
                >
                  {!file.is_folder && (
                    <div className="file-checkbox">
                      <input
                        type="checkbox"
                        checked={isFileSelected(file.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleFileSelection(file.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                  <div className="file-actions">
                    {!file.is_folder && (
                      <>
                        {canPreview(file) && (
                          <button
                            className="action-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              openPreviewModal(file);
                            }}
                            title="Preview"
                          >
                            👁️
                          </button>
                        )}
                        <button
                          className="action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(file);
                          }}
                          title="Download"
                        >
                          ⬇
                        </button>
                        <button
                          className="action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            openShareModal(file);
                          }}
                          title="Share"
                        >
                          🔗
                        </button>
                      </>
                    )}
                    {(hasPermission('file-sharing:write') || file.uploaded_by === user?.id) && (
                      <>
                        <button
                          className="action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRenameModal(file);
                          }}
                          title="Rename"
                        >
                          ✏️
                        </button>
                        <button
                          className="action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(file);
                          }}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                  <div className="file-icon">{getFileIcon(file)}</div>
                  <div className="file-name" title={file.original_name}>
                    {file.original_name}
                  </div>
                  <div className="file-meta">
                    <span>{file.is_folder ? 'Folder' : formatFileSize(file.size)}</span>
                    <span>{formatDate(file.created_at)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
                <div className="empty-icon">📁</div>
                <h3>No Files Yet</h3>
                <p>Upload your first file or create a folder to get started</p>
              </div>
            )}
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="files-list">
            {filteredFiles.length > 0 ? (
              <table className="files-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            selectAll();
                          } else {
                            clearSelection();
                          }
                        }}
                        checked={selectedFiles.length > 0 && selectedFiles.length === filteredFiles.filter(f => !f.is_folder).length}
                      />
                    </th>
                    <th>Name</th>
                    <th>Size</th>
                    <th>Modified</th>
                    <th>Uploaded By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFiles.map((file) => (
                    <tr
                      key={file.id}
                      className={`${file.is_folder && dragOverFolderId === file.id ? 'drag-over-folder' : ''} ${draggingFile?.id === file.id ? 'dragging' : ''} ${!file.is_folder && isFileSelected(file.id) ? 'selected' : ''}`}
                      onDoubleClick={() => file.is_folder ? openFolder(file) : canPreview(file) && openPreviewModal(file)}
                      draggable={!file.is_folder && hasPermission('file-sharing:write')}
                      onDragStart={(e) => handleFileDragStart(e, file)}
                      onDragEnd={handleFileDragEnd}
                      {...(file.is_folder && hasPermission('file-sharing:write') ? {
                        onDragOver: (e) => handleFolderDragOver(e, file.id),
                        onDragLeave: (e) => handleFolderDragLeave(e, file.id),
                        onDrop: (e) => handleFolderDrop(e, file)
                      } : {})}
                    >
                      <td>
                        {!file.is_folder && (
                          <input
                            type="checkbox"
                            checked={isFileSelected(file.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleFileSelection(file.id);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                      </td>
                      <td className="file-name-cell">
                        <span className="file-type-icon">{getFileIcon(file)}</span>
                        <span>{file.original_name}</span>
                      </td>
                      <td>{file.is_folder ? 'Folder' : formatFileSize(file.size)}</td>
                      <td>{formatDate(file.created_at)}</td>
                      <td>{file.uploaded_by_name || 'Unknown'}</td>
                      <td>
                        {file.is_folder ? (
                          <button
                            className="action-btn"
                            onClick={() => openFolder(file)}
                          >
                            Open
                          </button>
                        ) : (
                          <>
                            {canPreview(file) && (
                              <button
                                className="action-btn"
                                onClick={() => openPreviewModal(file)}
                              >
                                Preview
                              </button>
                            )}
                            <button
                              className="action-btn"
                              onClick={() => handleDownload(file)}
                            >
                              Download
                            </button>
                            <button
                              className="action-btn"
                              onClick={() => openShareModal(file)}
                            >
                              Share
                            </button>
                          </>
                        )}
                        {(hasPermission('file-sharing:write') || file.uploaded_by === user?.id) && (
                          <>
                            <button
                              className="action-btn"
                              onClick={() => openRenameModal(file)}
                            >
                              Rename
                            </button>
                            <button
                              className="action-btn"
                              onClick={() => handleDelete(file)}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">📁</div>
                <h3>No Files Yet</h3>
                <p>Upload your first file or create a folder to get started</p>
              </div>
            )}
          </div>
        )}
        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="pagination-controls">
            <button
              className="btn btn-secondary"
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Prev
            </button>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - pagination.page) <= 2)
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === '...' ? (
                  <span key={`ellipsis-${idx}`} className="pagination-ellipsis">...</span>
                ) : (
                  <button
                    key={item}
                    className={`btn ${item === pagination.page ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handlePageChange(item)}
                  >
                    {item}
                  </button>
                )
              )}
            <button
              className="btn btn-secondary"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </button>
            <span className="pagination-info">
              {pagination.total} total items
            </span>
          </div>
        )}
      </div>

      {/* Create Folder Modal */}
      {createFolderModalOpen && (
        <div className="modal-overlay" onClick={() => setCreateFolderModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Folder</h2>
              <button className="modal-close" onClick={() => setCreateFolderModalOpen(false)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleCreateFolder}>
                <div className="form-group">
                  <label htmlFor="folderName">Folder Name</label>
                  <input
                    type="text"
                    id="folderName"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Enter folder name"
                    autoFocus
                  />
                </div>
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">
                    Create
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setCreateFolderModalOpen(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {renameModalOpen && (
        <div className="modal-overlay" onClick={closeRenameModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Rename {selectedFile?.is_folder ? 'Folder' : 'File'}</h2>
              <button className="modal-close" onClick={closeRenameModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleRename}>
                <div className="form-group">
                  <label htmlFor="newName">New Name</label>
                  <input
                    type="text"
                    id="newName"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Enter new name"
                    autoFocus
                  />
                </div>
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">
                    Rename
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeRenameModal}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewModalOpen && selectedFile && (
        <div className="modal-overlay" onClick={closePreviewModal}>
          <div className="modal-content modal-preview" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedFile.original_name}</h2>
              <button className="modal-close" onClick={closePreviewModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              {renderPreview(selectedFile)}
            </div>

            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => handleDownload(selectedFile)}
              >
                Download
              </button>
              <button className="btn btn-secondary" onClick={closePreviewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareModalOpen && (
        <div className="modal-overlay" onClick={closeShareModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Share "{selectedFile?.original_name}"</h2>
              <button className="modal-close" onClick={closeShareModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <form onSubmit={handleShare}>
                <div className="form-group">
                  <label htmlFor="shareEmail">Share with user (email)</label>
                  <input
                    type="email"
                    id="shareEmail"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    placeholder="user@example.com"
                  />
                </div>
                <button type="submit" className="btn btn-primary">
                  Share
                </button>
              </form>

              {sharedUsers.length > 0 && (
                <div className="shared-users-list">
                  <h3>Shared with:</h3>
                  {sharedUsers.map((sharedUser) => (
                    <div key={sharedUser.id} className="shared-user-item">
                      <span>{sharedUser.email}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closeShareModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
